import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import superagent from 'superagent';
import { toast } from 'react-toastify';

import { backendUrl } from '@helpers/consts.js';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog';
import { Input as InputShadcn } from '@shadcn/components/ui/input';
import { Button } from '@shadcn/components/ui/button';
import { Label } from '@shadcn/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/ui/select';
import CustomSelect from '@shadcn/components/ui/react-select';

const normalizeLocationTypes = (types) => {
  if (!Array.isArray(types)) {
    return [];
  }

  return types.map((entry) => {
    if (typeof entry === 'string') {
      return {
        label: entry,
        value: entry,
      };
    }

    if (entry && typeof entry === 'object') {
      if (entry.value) {
        return {
          label: entry.label || entry.value,
          value: entry.value,
        };
      }

      if (entry.code) {
        return {
          label: entry.name || entry.code,
          value: entry.code,
        };
      }
    }

    const fallbackValue = String(entry ?? '');
    return {
      label: fallbackValue,
      value: fallbackValue,
    };
  });
};

const normalizeParentOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.map((option) => {
    if (typeof option === 'string') {
      return {
        label: option,
        value: option,
      };
    }

    if (option && typeof option === 'object') {
      const codeValue = option.code || option.value || '';
      const nameValue = option.name || option.label || codeValue;
      const typeSuffix = option.type ? ` (${option.type})` : '';

      return {
        label: `${nameValue}${codeValue ? ` - ${codeValue}` : ''}${typeSuffix}`,
        value: codeValue,
      };
    }

    const fallbackValue = String(option ?? '');
    return {
      label: fallbackValue,
      value: fallbackValue,
    };
  });
};

const extractErrorMessage = (error) => {
  if (!error) {
    return 'Unexpected error';
  }

  const responseMessage = error.response?.body?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim().length > 0) {
    return responseMessage;
  }

  if (Array.isArray(responseMessage) && responseMessage.length > 0) {
    return responseMessage[0];
  }

  return error.message || 'Unexpected error';
};

const defaultValues = {
  code: '',
  name: '',
  type: '',
  parentCode: '',
};

const LocationHierarchyCreateModal = ({
  open,
  onOpenChange,
  locationCode,
  locationName,
  onCreated,
}) => {
  const [locationTypes, setLocationTypes] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [optionError, setOptionError] = useState('');

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues,
  });

  const rootLocationCode = useMemo(() => locationCode || '', [locationCode]);

  const fetchOptions = useCallback(async (selectedType) => {
    if (!rootLocationCode) {
      return;
    }

    setOptionsLoading(true);
    setOptionError('');

    setLocationTypes([]);
    setParentOptions([]);

    try {
      let requestUrl = `${backendUrl}/hierarchy/${rootLocationCode}/options`;
      if (selectedType) {
        const encodedType = encodeURIComponent(selectedType);
        requestUrl = `${requestUrl}?type=${encodedType}`;
      }

      const response = await superagent
        .get(requestUrl)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      const parsedBody = response.body || {};
      const optionsPayload = parsedBody.data && typeof parsedBody.data === 'object'
        ? parsedBody.data
        : parsedBody;

      const normalizedTypes = normalizeLocationTypes(optionsPayload.locationTypes);
      if (normalizedTypes.length > 0) {
        setLocationTypes(normalizedTypes);
      }

      setParentOptions(normalizeParentOptions(optionsPayload.parentOptions));
    } catch (error) {
      setOptionError(extractErrorMessage(error));
    } finally {
      setOptionsLoading(false);
    }
  }, [rootLocationCode]);

  useEffect(() => {
    if (!open) {
      return;
    }

    fetchOptions();
    reset(defaultValues);
  }, [open, fetchOptions, reset]);

  const codeValue = watch('code');

  useEffect(() => {
    if (!codeValue) {
      return;
    }

    const upperCaseValue = codeValue.toUpperCase();
    if (upperCaseValue !== codeValue) {
      setValue('code', upperCaseValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [codeValue, setValue]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }

    onOpenChange(false);
  }, [onOpenChange, submitting]);

  const handleDialogChange = useCallback(
    (nextOpen) => {
      if (submitting) {
        return;
      }

      if (typeof nextOpen === 'boolean') {
        onOpenChange(nextOpen);
        if (!nextOpen) {
          reset(defaultValues);
        }
      }
    },
    [onOpenChange, reset, submitting]
  );

  const onSubmit = async (formValues) => {
    if (!rootLocationCode) {
      toast.error('Missing root location code');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...formValues,
        rootLocationCode,
        parentCode: formValues.parentCode || undefined,
      };

      await superagent
        .post(`${backendUrl}/hierarchy`)
        .send(payload)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');

      toast.success('Hierarchy node created successfully');
      reset(defaultValues);
      onOpenChange(false);
      if (onCreated) {
        onCreated();
      }
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel = submitting ? 'Creating...' : 'Create Node';

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {locationName ? `Create hierarchy for ${locationName}` : 'Create hierarchy node'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {optionsLoading && (
            <div className="text-sm text-muted-foreground">Loading options...</div>
          )}

          {optionError && (
            <div className="text-sm text-destructive">{optionError}</div>
          )}

          <div>
            <Label htmlFor="code">Code</Label>
            <InputShadcn
              id="code"
              placeholder="Example: WH001-ZA-A01-B01"
              {...register('code', {
                required: 'Code is required',
                pattern: {
                  value: /^[A-Z0-9-]+$/,
                  message: 'Code accepts only uppercase letters, numbers, and hyphen',
                },
                maxLength: {
                  value: 50,
                  message: 'Code must not exceed 50 characters',
                },
              })}
            />
            {errors.code && (
              <p className="mt-2 text-xs text-destructive">{errors.code.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="name">Name</Label>
            <InputShadcn
              id="name"
              placeholder="Node name"
              {...register('name', {
                required: 'Name is required',
                maxLength: {
                  value: 100,
                  message: 'Name must not exceed 100 characters',
                },
              })}
            />
            {errors.name && (
              <p className="mt-2 text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label>Type</Label>
            <Controller
              name="type"
              control={control}
              rules={{ required: 'Type is required' }}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    setValue('parentCode', '', { shouldDirty: true });
                    fetchOptions(value);
                  }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="z-[1500]">
                    {locationTypes.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type && (
              <p className="mt-2 text-xs text-destructive">{errors.type.message}</p>
            )}
          </div>

          <div>
            <Label>Parent</Label>
            <Controller
              name="parentCode"
              control={control}
              render={({ field }) => (
                  <CustomSelect
                    className="mt-2"
                    placeholder="Select parent (optional)"
                    isClearable
                    isDisabled={!watch('type') || optionsLoading}
                    options={parentOptions}
                    value={parentOptions.find((option) => option.value === field.value) || null}
                    onChange={(selected) => field.onChange(selected ? selected.value : '')}
                  />
              )}
            />
          </div>

          <DialogFooter className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || optionsLoading}>
              {actionLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LocationHierarchyCreateModal;
